import { useEffect, useState, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { Users, DollarSign, Activity, Calendar } from 'lucide-react'
import { cn } from '@/lib/utils'

const PROJECTS = [
  { id: 'signedwall', name: 'SignedWall' },
  { id: 'streamviewerbot', name: 'StreamViewerBot' },
] as const

interface User {
  [key: string]: any
}

interface Transaction {
  [key: string]: any
}

interface ProjectData {
  users: User[]
  transactions: Transaction[]
  orders?: any[]
}

// Helper to find date field in an object
const findDateValue = (obj: Record<string, any>): Date | null => {
  const dateFields = ['created_at', 'createdAt', 'date', 'timestamp', 'created', 'updated_at', 'updatedAt', 'order_date', 'orderDate', 'payment_date', 'paymentDate']
  for (const field of dateFields) {
    if (obj[field]) {
      const date = new Date(obj[field])
      if (!isNaN(date.getTime())) return date
    }
  }
  return null
}

// Helper to find price/amount value in an object
const findPriceValue = (obj: Record<string, any>, projectId?: string): number => {
  if (obj.amount_cents !== undefined) {
    const val = (typeof obj.amount_cents === 'number' ? obj.amount_cents : parseFloat(obj.amount_cents) || 0) / 100
    // For signedwall, only count positive values
    if (projectId === 'signedwall' && val < 0) return 0
    return val
  }
  
  const priceFields = ['amount', 'price', 'cost', 'total', 'value', 'payment_amount', 'order_total']
  for (const field of priceFields) {
    if (obj[field] !== undefined) {
      const val = typeof obj[field] === 'number' ? obj[field] : parseFloat(obj[field]) || 0
      if (projectId === 'signedwall' && val < 0) return 0
      return val
    }
  }
  return 0
}

// Date filter component
interface DateFilterProps {
  startDate: string
  endDate: string
  onStartDateChange: (date: string) => void
  onEndDateChange: (date: string) => void
  onClear: () => void
}

function DateFilter({ startDate, endDate, onStartDateChange, onEndDateChange, onClear }: DateFilterProps) {
  return (
    <div className="flex flex-wrap items-center gap-3 bg-gray-50 p-3 rounded-lg">
      <div className="flex items-center gap-2">
        <Calendar className="h-4 w-4 text-gray-500" />
        <span className="text-sm font-medium text-gray-700">Filter by date:</span>
      </div>
      <div className="flex items-center gap-2">
        <label className="text-sm text-gray-600">From:</label>
        <input
          type="date"
          value={startDate}
          onChange={(e) => onStartDateChange(e.target.value)}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        />
      </div>
      <div className="flex items-center gap-2">
        <label className="text-sm text-gray-600">To:</label>
        <input
          type="date"
          value={endDate}
          onChange={(e) => onEndDateChange(e.target.value)}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        />
      </div>
      {(startDate || endDate) && (
        <button
          onClick={onClear}
          className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-md transition-colors"
        >
          Clear
        </button>
      )}
    </div>
  )
}

export default function ProjectPage() {
  const { id } = useParams<{ id: string }>()
  const [data, setData] = useState<ProjectData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'transactions' | 'orders'>('overview')
  
  // Date filter state
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  const project = PROJECTS.find((p) => p.id === id)

  useEffect(() => {
    if (!id || !project) {
      setError('Project not found')
      setLoading(false)
      return
    }

    const fetchData = async () => {
      try {
        const response = await fetch(`/api/project/${id}`)
        if (!response.ok) {
          throw new Error('Failed to fetch project data')
        }
        const result = await response.json() as ProjectData
        setData(result)
      } catch (err) {
        setError((err as Error).message)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [id, project])

  const stats = useMemo(() => {
    if (!data) return { users: 0, revenue: 0, recentActivity: 0 }

    const userCount = data.users.length
    
    // Try to find a revenue field
    const revenue = data.transactions.reduce((acc, tx) => {
      return acc + findPriceValue(tx, id)
    }, 0)

    return {
      users: userCount,
      revenue: revenue,
      recentActivity: data.transactions.length
    }
  }, [data, id])

  // Filter function for date range
  const filterByDateRange = <T extends Record<string, any>>(items: T[]): T[] => {
    if (!startDate && !endDate) return items
    
    return items.filter(item => {
      const itemDate = findDateValue(item)
      if (!itemDate) return true // Include items without dates
      
      const start = startDate ? new Date(startDate) : null
      const end = endDate ? new Date(endDate + 'T23:59:59') : null
      
      if (start && itemDate < start) return false
      if (end && itemDate > end) return false
      return true
    })
  }

  // Filtered transactions and orders
  const filteredTransactions = useMemo(() => {
    if (!data) return []
    return filterByDateRange(data.transactions)
  }, [data, startDate, endDate])

  const filteredOrders = useMemo(() => {
    if (!data?.orders) return []
    return filterByDateRange(data.orders)
  }, [data, startDate, endDate])

  // Calculate totals for filtered data
  const transactionsTotal = useMemo(() => {
    return filteredTransactions.reduce((acc, tx) => acc + findPriceValue(tx, id), 0)
  }, [filteredTransactions, id])

  const ordersTotal = useMemo(() => {
    return filteredOrders.reduce((acc, order) => acc + findPriceValue(order, id), 0)
  }, [filteredOrders, id])

  const clearDateFilter = () => {
    setStartDate('')
    setEndDate('')
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  if (error || !project) {
    return (
      <div className="p-4 rounded-md bg-red-50 text-red-700">
        Error: {error || 'Project not found'}
      </div>
    )
  }

  if (!data) {
    return <div>No data available</div>
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">{project.name} Dashboard</h1>
        <div className="flex space-x-2 bg-gray-100 p-1 rounded-lg">
          {(['overview', 'users', 'transactions', ...(id === 'streamviewerbot' ? ['orders'] : [])] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as any)}
              className={cn(
                "px-4 py-2 text-sm font-medium rounded-md transition-colors capitalize",
                activeTab === tab
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-900"
              )}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'overview' && (
        <div className="space-y-8">
          {/* Stats Grid */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Users className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Total Users</dt>
                      <dd className="text-lg font-medium text-gray-900">{stats.users}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <DollarSign className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Total Revenue</dt>
                      <dd className="text-lg font-medium text-gray-900">
                        ${stats.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Activity className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Recent Transactions</dt>
                      <dd className="text-lg font-medium text-gray-900">{stats.recentActivity}</dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Activity Preview */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Recent Activity</h3>
            </div>
            <div className="bg-gray-50 px-4 py-5 sm:p-6">
              <div className="flow-root">
                <ul className="-mb-8">
                  {data.transactions.slice(0, 5).map((tx, idx) => (
                    <li key={idx}>
                      <div className="relative pb-8">
                        {idx !== 4 && <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true"></span>}
                        <div className="relative flex space-x-3">
                          <div>
                            <span className="h-8 w-8 rounded-full bg-green-500 flex items-center justify-center ring-8 ring-white">
                              <DollarSign className="h-5 w-5 text-white" />
                            </span>
                          </div>
                          <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                            <div>
                              <p className="text-sm text-gray-500">
                                Payment received <span className="font-medium text-gray-900">
                                  ${tx.amount_cents ? (tx.amount_cents / 100).toFixed(2) : (tx.amount || tx.price || tx.cost || 0)}
                                </span>
                              </p>
                            </div>
                            <div className="text-right text-sm whitespace-nowrap text-gray-500">
                              <time dateTime={tx.created_at}>{new Date(tx.created_at || Date.now()).toLocaleDateString()}</time>
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-6">
                <button
                  onClick={() => setActiveTab('transactions')}
                  className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  View all transactions
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Users Directory</h3>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
              {data.users.length} users
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {data.users.length > 0 && Object.keys(data.users[0]).map((key) => (
                    <th
                      key={key}
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {key.replace(/_/g, ' ')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.users.map((user, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    {Object.values(user).map((val: any, j) => (
                      <td key={j} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {typeof val === 'object' ? JSON.stringify(val) : String(val)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'transactions' && (
        <div className="space-y-4">
          <DateFilter
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onClear={clearDateFilter}
          />
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex flex-wrap justify-between items-center gap-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Transaction History</h3>
              <div className="flex items-center gap-4">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  {filteredTransactions.length} transactions
                  {(startDate || endDate) && ` (filtered from ${data.transactions.length})`}
                </span>
                {transactionsTotal > 0 && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-indigo-100 text-indigo-800">
                    Total: ${transactionsTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                )}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {filteredTransactions.length > 0 && Object.keys(filteredTransactions[0]).map((key) => (
                      <th
                        key={key}
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {key.replace(/_/g, ' ')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredTransactions.map((tx, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      {Object.values(tx).map((val: any, j) => (
                        <td key={j} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {typeof val === 'object' ? JSON.stringify(val) : String(val)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredTransactions.length === 0 && (
                <div className="px-6 py-8 text-center text-gray-500">
                  No transactions found for the selected date range.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'orders' && data.orders && (
        <div className="space-y-4">
          <DateFilter
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onClear={clearDateFilter}
          />
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-4 py-5 sm:px-6 border-b border-gray-200 flex flex-wrap justify-between items-center gap-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Orders</h3>
              <div className="flex items-center gap-4">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {filteredOrders.length} orders
                  {(startDate || endDate) && ` (filtered from ${data.orders.length})`}
                </span>
                {ordersTotal > 0 && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-indigo-100 text-indigo-800">
                    Total: ${ordersTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                )}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {filteredOrders.length > 0 && Object.keys(filteredOrders[0]).map((key) => (
                      <th
                        key={key}
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {key.replace(/_/g, ' ')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredOrders.map((order, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      {Object.values(order).map((val: any, j) => (
                        <td key={j} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {typeof val === 'object' ? JSON.stringify(val) : String(val)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredOrders.length === 0 && (
                <div className="px-6 py-8 text-center text-gray-500">
                  No orders found for the selected date range.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
